import React, { useState, useEffect } from 'react'
import {
  Typography,
  Card,
  Row,
  Col,
  Tabs,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Statistic,
  Alert,
  Spin,
  message,
  InputNumber,
} from 'antd'
import {
  UserOutlined,
  FileTextOutlined,
  BarChartOutlined,
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExportOutlined,
} from '@ant-design/icons'
import { apiService } from '../services/apiService'
import { useAppSelector } from '../hooks/redux'
import { isAdminUser } from '../utils/adminUtils'
import CompletedTodayCard from './CompletedTodayCard'

interface User {
  id: number
  email: string
  name: string
  is_admin: boolean
  is_active: boolean
  daily_leads_completed: number
  date_joined: string
  last_lead_date: string | null
}

interface Lead {
  id: number
  company_name: string
  qualification_status: string
  leadgen_status: string | null
  assigned_to: string | null
  assigned_at: string | null
  lead_score: number
  source_type: string
  created_at: string
  palladium_link?: string | null
  crm_link?: string | null
  crm_company_key?: string | null
  good_emails_count?: number
  generic_emails_count?: number
  phone_numbers_count?: number
  generic_phones_count?: number
  primary_source?: string | null
  is_verified?: boolean
  not_qualified_reason?: string | null
  completed_at?: string | null
}

interface AdminStats {
  total_users: number
  active_users: number
  admin_users: number
  total_leads: number
  qualified_leads: number
  completed_leads: number
  completed_today: number
  successful_leads: number
  unsuccessful_leads: number
  pending_leads: number
  disqualified_leads: number
  available_leads: number
  locked_leads: number
}

function AdminPanel() {
  const { user } = useAppSelector((state) => state.auth)
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [leads, setLeads] = useState<Lead[]>([])
  const [filteredLeads, setFilteredLeads] = useState<Lead[]>([])
  const [adminStats, setAdminStats] = useState<AdminStats | null>(null)
  const [selectedTab, setSelectedTab] = useState('overview')
  const [leadStatusFilter, setLeadStatusFilter] = useState<string>('all')
  
  // Modal states
  const [editUserModal, setEditUserModal] = useState(false)
  const [editLeadModal, setEditLeadModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  
  // Forms
  const [userForm] = Form.useForm()
  const [leadForm] = Form.useForm()

  useEffect(() => {
    if (selectedTab === 'overview') {
      fetchAdminStats()
    } else if (selectedTab === 'users') {
      fetchUsers()
    } else if (selectedTab === 'leads') {
      fetchLeads()
    }
  }, [selectedTab])

  // Initialize filtered leads when leads change
  useEffect(() => {
    applyFilters(leads, leadStatusFilter)
  }, [leads, leadStatusFilter])

  const fetchAdminStats = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/v1/admin/stats/', {
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
        }
      })
      if (response.ok) {
        const stats = await response.json()
        setAdminStats(stats)
      } else {
        const error = await response.text()
        console.error('Failed to fetch admin stats:', error)
        message.error('Failed to fetch admin statistics')
      }
    } catch (error) {
      console.error('Failed to fetch admin stats:', error)
      message.error('Network error while fetching admin statistics')
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/v1/admin/users/', {
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        setUsers(data.results || data)
      }
    } catch (error) {
      console.error('Failed to fetch users:', error)
      message.error('Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }

  const fetchLeads = async () => {
    try {
      setLoading(true)
      let allLeads: Lead[] = []
      let nextUrl: string | null = '/api/v1/admin/leads/'
      
      // Fetch all pages of leads
      while (nextUrl) {
        const response: Response = await fetch(nextUrl, {
          credentials: 'include'
        })
        
        if (response.ok) {
          const data: any = await response.json()
          
          // If it's paginated data (has results field), handle pagination
          if (data.results) {
            allLeads = [...allLeads, ...data.results]
            nextUrl = data.next
          } else {
            // If it's not paginated (direct array), add all and stop
            allLeads = [...allLeads, ...(Array.isArray(data) ? data : [data])]
            nextUrl = null
          }
        } else {
          console.error('Failed to fetch leads page:', await response.text())
          break
        }
      }
      
      setLeads(allLeads)
    } catch (error) {
      console.error('Failed to fetch leads:', error)
      message.error('Failed to fetch leads')
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = (leadsData: Lead[], statusFilter: string) => {
    let filtered = [...leadsData]
    
    if (statusFilter !== 'all') {
      if (statusFilter === 'successful') {
        filtered = filtered.filter(lead => lead.leadgen_status === 'successful')
      } else if (statusFilter === 'unsuccessful') {
        filtered = filtered.filter(lead => lead.leadgen_status === 'unsuccessful')
      } else if (statusFilter === 'completed') {
        filtered = filtered.filter(lead => lead.leadgen_status !== null)
      } else if (statusFilter === 'locked') {
        filtered = filtered.filter(lead => lead.assigned_to !== null && lead.leadgen_status === null)
      } else if (statusFilter === 'available') {
        filtered = filtered.filter(lead => 
          lead.qualification_status === 'qualified' && 
          lead.assigned_to === null && 
          lead.leadgen_status === null
        )
      }
    }
    
    setFilteredLeads(filtered)
  }

  const handleStatusFilterChange = (value: string) => {
    setLeadStatusFilter(value)
    applyFilters(leads, value)
  }

  const handleEditUser = (user: User) => {
    setSelectedUser(user)
    userForm.setFieldsValue({
      name: user.name,
      is_admin: user.is_admin,
      is_active: user.is_active,
    })
    setEditUserModal(true)
  }

  const handleEditLead = (lead: Lead) => {
    setSelectedLead(lead)
    leadForm.setFieldsValue({
      company_name: lead.company_name,
      qualification_status: lead.qualification_status,
      lead_score: lead.lead_score,
      source_type: lead.source_type,
    })
    setEditLeadModal(true)
  }

  const handleUserUpdate = async (values: any) => {
    try {
      const response = await fetch(`/api/v1/admin/users/${selectedUser!.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(values),
      })
      
      if (response.ok) {
        message.success('User updated successfully')
        setEditUserModal(false)
        fetchUsers()
      } else {
        message.error('Failed to update user')
      }
    } catch (error) {
      console.error('Failed to update user:', error)
      message.error('Failed to update user')
    }
  }

  const handleLeadUpdate = async (values: any) => {
    try {
      const response = await fetch(`/api/v1/admin/leads/${selectedLead!.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(values),
      })
      
      if (response.ok) {
        message.success('Lead updated successfully')
        setEditLeadModal(false)
        fetchLeads()
      } else {
        message.error('Failed to update lead')
      }
    } catch (error) {
      console.error('Failed to update lead:', error)
      message.error('Failed to update lead')
    }
  }

  const userColumns = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string, record: User) => (
        <Space>
          <UserOutlined />
          {email}
          {record.is_admin && <Tag color="red">Admin</Tag>}
          {!record.is_active && <Tag color="gray">Inactive</Tag>}
        </Space>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Daily Completed',
      dataIndex: 'daily_leads_completed',
      key: 'daily_leads_completed',
    },
    {
      title: 'Last Lead Date',
      dataIndex: 'last_lead_date',
      key: 'last_lead_date',
      render: (date: string | null) => date || 'Never',
    },
    {
      title: 'Joined',
      dataIndex: 'date_joined',
      key: 'date_joined',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: User) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            size="small" 
            onClick={() => handleEditUser(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ]

  const leadColumns = [
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (name: string, record: Lead) => (
        <Space>
          <FileTextOutlined />
          {name}
        </Space>
      ),
    },
    {
      title: 'Links',
      key: 'links',
      width: 150,
      render: (_: any, record: Lead) => (
        <Space size={4} wrap>
          <Button
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => window.open(`/lead/${record.id}`, '_blank')}
            style={{
              backgroundColor: '#f0f8ff',
              borderColor: '#b3d9e8',
              color: '#0066cc',
              fontSize: '11px',
              padding: '2px 8px',
              height: '24px',
            }}
            title="Open Lead Detail Page"
          >
            Lead
          </Button>
          {record.palladium_link && (
            <Button
              size="small"
              icon={<ExportOutlined />}
              onClick={() => window.open(record.palladium_link!, '_blank')}
              style={{
                backgroundColor: '#e8f4f8',
                borderColor: '#b3d9e8',
                color: '#0066cc',
                fontSize: '11px',
                padding: '2px 8px',
                height: '24px',
              }}
              title="Open in Palladium"
            >
              Palladium
            </Button>
          )}
          {record.crm_link && (
            <Button
              size="small"
              icon={<ExportOutlined />}
              onClick={() => window.open(record.crm_link!, '_blank')}
              style={{
                backgroundColor: '#fff4e8',
                borderColor: '#ffd4a3',
                color: '#ff6600',
                fontSize: '11px',
                padding: '2px 8px',
                height: '24px',
              }}
              title="Open in CRM"
            >
              CRM
            </Button>
          )}
          {!record.crm_company_key && !record.palladium_link && !record.crm_link && (
            <span style={{ color: '#999', fontSize: '11px' }}>No links</span>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'leadgen_status',
      key: 'leadgen_status',
      render: (status: string | null) => (
        <Tag color={status === 'successful' ? 'green' : status === 'unsuccessful' ? 'red' : 'blue'}>
          {status || 'pending'}
        </Tag>
      ),
    },
    {
      title: 'Completion Details',
      key: 'completion_details',
      width: 200,
      render: (_: any, record: Lead) => {
        if (!record.leadgen_status) {
          return <span style={{ color: '#999', fontSize: '12px' }}>Not completed</span>
        }
        
        const hasData = record.good_emails_count || record.generic_emails_count || record.phone_numbers_count || record.generic_phones_count
        
        if (record.leadgen_status === 'successful' && hasData) {
          return (
            <div style={{ fontSize: '12px' }}>
              {(record.good_emails_count || 0) > 0 && (
                <div>✉️ {record.good_emails_count} good emails</div>
              )}
              {(record.generic_emails_count || 0) > 0 && (
                <div>📧 {record.generic_emails_count} generic emails</div>
              )}
              {(record.phone_numbers_count || 0) > 0 && (
                <div>📞 {record.phone_numbers_count} phone numbers</div>
              )}
              {(record.generic_phones_count || 0) > 0 && (
                <div>📱 {record.generic_phones_count} generic phones</div>
              )}
              {record.primary_source && (
                <div style={{ color: '#666' }}>Source: {record.primary_source}</div>
              )}
              {record.is_verified && (
                <div style={{ color: 'green' }}>✅ Verified</div>
              )}
            </div>
          )
        } else if (record.leadgen_status === 'unsuccessful') {
          return (
            <div style={{ fontSize: '12px' }}>
              {record.not_qualified_reason && (
                <div style={{ color: '#ff6600' }}>
                  ❌ {record.not_qualified_reason}
                </div>
              )}
              {!record.not_qualified_reason && (
                <div style={{ color: '#666' }}>No reason provided</div>
              )}
            </div>
          )
        }
        
        return (
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.leadgen_status === 'successful' ? 'No details available' : 'Completed'}
          </div>
        )
      },
    },
    {
      title: 'Assigned To',
      dataIndex: 'assigned_to',
      key: 'assigned_to',
      render: (email: string | null) => email || 'Unassigned',
    },
    {
      title: 'Score',
      dataIndex: 'lead_score',
      key: 'lead_score',
    },
    {
      title: 'Source',
      dataIndex: 'source_type',
      key: 'source_type',
      render: (type: string) => (
        <Tag color={type === 'palladium' ? 'blue' : type === 'crm' ? 'purple' : 'cyan'}>
          {type}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Lead) => (
        <Space>
          <Button 
            icon={<EditOutlined />} 
            size="small" 
            onClick={() => handleEditLead(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ]

  if (!isAdminUser(user)) {
    return (
      <Alert
        message="Access Denied"
        description="You must be an admin user with a primenumbers.in email to access this panel."
        type="error"
        showIcon
      />
    )
  }

  const tabItems = [
    {
      key: 'overview',
      label: (
        <span>
          <BarChartOutlined />
          Overview
        </span>
      ),
      children: (
        <div>
          {loading ? (
            <Spin size="large" style={{ display: 'flex', justifyContent: 'center', margin: 50 }} />
          ) : (
            <>
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                  <Card>
                    <Statistic
                      title="Total Users"
                      value={adminStats?.total_users || 0}
                      prefix={<UserOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card>
                    <Statistic
                      title="Admin Users"
                      value={adminStats?.admin_users || 0}
                      prefix={<SettingOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card>
                    <Statistic
                      title="Total Leads"
                      value={adminStats?.total_leads || 0}
                      prefix={<FileTextOutlined />}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <CompletedTodayCard stats={adminStats} />
                </Col>
              </Row>
              
              {/* Lead Status Statistics */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                  <Card>
                    <Statistic
                      title="Successful"
                      value={adminStats?.successful_leads || 0}
                      valueStyle={{ color: '#52c41a' }}
                      prefix={<span style={{ color: '#52c41a' }}>✅</span>}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card>
                    <Statistic
                      title="Unsuccessful"
                      value={adminStats?.unsuccessful_leads || 0}
                      valueStyle={{ color: '#ff4d4f' }}
                      prefix={<span style={{ color: '#ff4d4f' }}>❌</span>}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card>
                    <Statistic
                      title="Pending"
                      value={adminStats?.pending_leads || 0}
                      valueStyle={{ color: '#fa8c16' }}
                      prefix={<span style={{ color: '#fa8c16' }}>⏳</span>}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Card>
                    <Statistic
                      title="Locked"
                      value={adminStats?.locked_leads || 0}
                      valueStyle={{ color: '#1890ff' }}
                      prefix={<span style={{ color: '#1890ff' }}>🔒</span>}
                    />
                  </Card>
                </Col>
              </Row>
              
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Card title="Lead Queue Status" extra={<Button icon={<ReloadOutlined />} onClick={fetchAdminStats} />}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>Available: <Tag color="green">{adminStats?.available_leads || 0}</Tag></div>
                      <div>Locked: <Tag color="orange">{adminStats?.locked_leads || 0}</Tag></div>
                      <div>Qualified: <Tag color="blue">{adminStats?.qualified_leads || 0}</Tag></div>
                      <div>Disqualified: <Tag color="red">{adminStats?.disqualified_leads || 0}</Tag></div>
                    </Space>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card title="System Status">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>Active Users: <Tag color="green">{adminStats?.active_users || 0}</Tag></div>
                      <div>Database: <Tag color="green">Connected</Tag></div>
                      <div>Admin Panel: <Tag color="green">Active</Tag></div>
                    </Space>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'users',
      label: (
        <span>
          <UserOutlined />
          Users
        </span>
      ),
      children: (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={fetchUsers}>
                Refresh
              </Button>
              <Typography.Text type="secondary">
                Manage users with primenumbers.in emails
              </Typography.Text>
            </Space>
          </div>
          <Table
            columns={userColumns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} users`,
            }}
          />
        </div>
      ),
    },
    {
      key: 'leads',
      label: (
        <span>
          <FileTextOutlined />
          Leads
        </span>
      ),
      children: (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={fetchLeads}>
                Refresh
              </Button>
              <Select
                value={leadStatusFilter}
                onChange={handleStatusFilterChange}
                style={{ width: 160 }}
                placeholder="Filter by status"
              >
                <Select.Option value="all">All Leads</Select.Option>
                <Select.Option value="successful">✅ Successful</Select.Option>
                <Select.Option value="unsuccessful">❌ Unsuccessful</Select.Option>
                <Select.Option value="completed">✅ Completed</Select.Option>
                <Select.Option value="locked">🔒 Locked</Select.Option>
                <Select.Option value="available">🟢 Available</Select.Option>
              </Select>
              <Typography.Text type="secondary">
                Manage lead assignments and status
              </Typography.Text>
              <Typography.Text type="secondary" style={{ marginLeft: 'auto' }}>
                Showing {filteredLeads.length} of {leads.length} leads
              </Typography.Text>
            </Space>
          </div>
          <Table
            columns={leadColumns}
            dataSource={filteredLeads}
            rowKey="id"
            loading={loading}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} ${leadStatusFilter === 'all' ? '' : `${leadStatusFilter} `}leads`,
            }}
          />
        </div>
      ),
    },
  ]

  return (
    <div>
      <Typography.Title level={2} style={{ marginBottom: 24 }}>
        Admin Panel
        <Typography.Text type="secondary" style={{ fontSize: 14, marginLeft: 8 }}>
          (primenumbers.in access only)
        </Typography.Text>
      </Typography.Title>

      <Tabs
        activeKey={selectedTab}
        onChange={setSelectedTab}
        items={tabItems}
        size="large"
      />

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={editUserModal}
        onCancel={() => setEditUserModal(false)}
        footer={null}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={handleUserUpdate}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter user name' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item name="is_admin" label="Admin Status" valuePropName="checked">
            <Select>
              <Select.Option value={true}>Admin</Select.Option>
              <Select.Option value={false}>Regular User</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item name="is_active" label="Active Status" valuePropName="checked">
            <Select>
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update User
              </Button>
              <Button onClick={() => setEditUserModal(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Lead Modal */}
      <Modal
        title="Edit Lead"
        open={editLeadModal}
        onCancel={() => setEditLeadModal(false)}
        footer={null}
      >
        <Form
          form={leadForm}
          layout="vertical"
          onFinish={handleLeadUpdate}
        >
          <Form.Item
            name="company_name"
            label="Company Name"
            rules={[{ required: true, message: 'Please enter company name' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item name="qualification_status" label="Qualification Status">
            <Select>
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="qualified">Qualified</Select.Option>
              <Select.Option value="disqualified">Disqualified</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item name="lead_score" label="Lead Score">
            <InputNumber min={0} max={100} />
          </Form.Item>
          
          <Form.Item name="source_type" label="Source Type">
            <Select>
              <Select.Option value="palladium">Palladium</Select.Option>
              <Select.Option value="crm">CRM</Select.Option>
              <Select.Option value="api">API</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Update Lead
              </Button>
              <Button onClick={() => setEditLeadModal(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default AdminPanel
